import lodash from 'lodash';
import {
  calculateChoiceSelectionsTotals,
  choiceSetsWithQuantities,
  extractChoiceWithQuantityFromNestedPurchased,
  nestedChoiceSetsWithQuantities,
  OrderingSelectors,
  tallyAndValidateChoiceSets,
  tallyAndValidateNestedChoicesets,
} from 'polygon-ordering';
import type { ReactElement } from 'react';
import React, { createContext, useEffect, useState } from 'react';
import { useAppSelector } from '../app/hooks';
import sortChoices from '../libs/polygon-ordering/src/utils/ordering/sortChoices';
import choiceSetDiscountPlus from '../libs/polygon-ordering/src/utils/ordering/choiceSetDiscountPlus';
import { getAllMiamChoiceSets } from '../libs/polygon-ordering/src/utils/ordering/calculateTotalsForPurchase';
import calculateMiamChoiceSetTotalPrice from '../libs/polygon-ordering/src/utils/ordering/calculateMIAMChoiceSetTotalPrice';
import syncFlatChoiceSelectionsIntoPurchased from '../libs/polygon-ordering/src/utils/ordering/syncFlatChoiceSelectionsIntoPurchased';
const {
  getOpenPurchase,
  getOpenPurchasePreviouslyStaged,
  getStagedPurchases,
  getNestedItemStockBalancesData,
  getMenu,
  getChoiceSetDiscountConfig,
} = OrderingSelectors;

export type NestedIngredientsStages = SDict<string | SDict<ChoiceWithQuantity[]>> | undefined;
export interface INestedChoiceSetsContext {
  showNestedItem: boolean;
  setShowNestedItem: (v: boolean) => void;
  showChoices: boolean;
  setShowChoices: (v: boolean) => void;
  dataForAdjust: any;
  setDataForAdjust: (data: any) => void;
  stages: NestedIngredientsStages;
  setStages: (v: NestedIngredientsStages) => void;
  purchased: NestedChoiceSelections | undefined;
  setPurchased: (v: NestedChoiceSelections | undefined) => void;
  isSelectionValid: boolean; //'add to item' button enable/disable
  setIsSelectionValid: (v: boolean) => void;
  nestedItemInValid: boolean; //'add to cart' button enable/disable
  setNestedItemInValid: (v: boolean) => void;
  nestedChoiceSetsTotal: number; //choicesets total price
  setNestedChoiceSetsTotal: (v: number) => void;
  scrollDownIndicator?: () => void;
  selectedChoiceSet?: ValidatedChoiceSet | null;
  setSelectedChoiceSet: (v: ValidatedChoiceSet) => void;
  selectedItems?: ValidatedChoiceSet[];
  setSelectItems?: (v: ValidatedChoiceSet[]) => void;
  miamModalClose: boolean;
  setMiamModalClose: (v: boolean) => void;
  miamChoiceSet?: ValidatedNestedChoiceSet;
  setMiamChoiceSet: (v: ValidatedNestedChoiceSet | undefined) => void;
}

export const NestedChoiceSetsContext = createContext<INestedChoiceSetsContext | null>(null);

export default ({ children }: { children: ReactElement }) => {
  const [showNestedItem, setShowNestedItem] = useState<boolean>(false);
  const [showChoices, setShowChoices] = useState<boolean>(false);
  const [stages, setStages] = useState<NestedIngredientsStages>(undefined);
  const [purchased, setPurchased] = useState<NestedChoiceSelections>(undefined);
  const [isSelectionValid, setIsSelectionValid] = useState(false);
  const [nestedItemInValid, setNestedItemInValid] = useState(false);
  const [nestedChoiceSetsTotal, setNestedChoiceSetsTotal] = useState(0);
  const [dataForAdjust, setDataForAdjust] = useState([]);
  //miam
  const [selectedChoiceSet, setSelectedChoiceSet] = useState<ValidatedChoiceSet | null>(null);
  const [selectedItems, setSelectItems] = useState<ValidatedChoiceSet[] | undefined>(undefined);
  const [miamModalClose, setMiamModalClose] = useState<boolean>(false);
  const [miamChoiceSet, setMiamChoiceSet] = useState<ValidatedNestedChoiceSet | undefined>(
    undefined,
  );

  const state = {
    showNestedItem,
    setShowNestedItem,
    showChoices,
    setShowChoices,
    dataForAdjust,
    setDataForAdjust,
    stages,
    setStages,
    purchased,
    setPurchased,
    isSelectionValid,
    setIsSelectionValid,
    nestedItemInValid,
    setNestedItemInValid,
    nestedChoiceSetsTotal,
    setNestedChoiceSetsTotal,
    selectedChoiceSet,
    setSelectedChoiceSet,
    selectedItems,
    setSelectItems,
    miamModalClose,
    setMiamModalClose,
    miamChoiceSet,
    setMiamChoiceSet,
  };
  const openPurchase = useAppSelector(getOpenPurchase);
  const choiceSetDiscountConfig = useAppSelector(getChoiceSetDiscountConfig);

  const choiceSets = openPurchase?.choiceSets;
  const previouslyStaged = useAppSelector(getOpenPurchasePreviouslyStaged);
  const [stockBalanceDataMap] = useAppSelector(getNestedItemStockBalancesData(openPurchase));
  const prePurchase = usePreSelections(
    openPurchase,
    choiceSets,
    stockBalanceDataMap,
    choiceSetDiscountConfig,
  );
  useEffect(() => {
    // don't redo preselections if there are already selections
    if (!previouslyStaged && !purchased) setPurchased(prePurchase);
  }, [prePurchase, previouslyStaged]);

  // Keep `purchased`'s flat-choiceset entries in sync with live Redux
  // selections once seeded (see syncFlatChoiceSelectionsIntoPurchased for why).
  // This is a targeted patch, not a switch to one shared source of truth like
  // mobile/kiosk have — that's a bigger refactor best left for the planned
  // web app rebuild.
  useEffect(() => {
    if (!purchased || !choiceSets) return;
    setPurchased(prev =>
      syncFlatChoiceSelectionsIntoPurchased(prev, choiceSets, openPurchase?.choiceSelections),
    );
  }, [choiceSets, openPurchase?.choiceSelections]);

  const menu = useAppSelector(getMenu);
  const items = menu?.items;
  const allChoiceSets = menu?.choiceSets as ChoiceSets | NestedChoiceSets;

  const stagedPurchases = useAppSelector(getStagedPurchases);
  useEffect(() => {
    // get the key of the selected nested choice set
    const currentSetIndx = (stages || {})[0] as string;
    let validatedSets: ValidatedChoiceSet[] = [];

    if (currentSetIndx && choiceSets) {
      // get the definition of the choiceset with that key
      const set = choiceSets.find(s => s.key === currentSetIndx);
      const nested = Object.keys(stages || {}).length;
      const currentSets =
        nested === 3
          ? (items || {})[(stages || {})[1] as string].choiceSets.map(e => allChoiceSets[e])
          : [allChoiceSets[(set as ChoiceSet).key]];

      let currentSelections = (stages || {})[nested - 1];
      currentSelections = typeof currentSelections === 'string' ? {} : currentSelections;
      const choiceSelections = lodash.mapValues(
        currentSelections as { [key: string]: ChoiceWithQuantity[] },
        values => {
          return lodash.map(values, v => v.id);
        },
      );
      const cwq = choiceSetsWithQuantities(currentSets, choiceSelections);
      validatedSets = tallyAndValidateChoiceSets(cwq, choiceSetDiscountConfig);
    }

    if (validatedSets) {
      setIsSelectionValid(validatedSets.filter(c => !c.valid).length === 0);
    }
  }, [stages]);
  const [selectionValid, choicesetsTotalPrice] = usePurchasedItemStatus(
    purchased,
    choiceSets ?? [],
    choiceSetDiscountConfig,
  );

  useEffect(() => {
    setNestedItemInValid(selectionValid);
    setNestedChoiceSetsTotal(choicesetsTotalPrice);
  }, [choiceSets, selectionValid, choicesetsTotalPrice]);

  useEffect(() => {
    // don't redo preselections if there are already selections
    if (purchased) return;
    if (previouslyStaged) {
      if (openPurchase && stagedPurchases) {
        const stagedPurchase = stagedPurchases.find(p => p.id === openPurchase.id);
        setPurchased(stagedPurchase?.choiceSelections as NestedChoiceSelections);
      }
    }
  }, [previouslyStaged]);

  return (
    <NestedChoiceSetsContext.Provider value={state}>{children}</NestedChoiceSetsContext.Provider>
  );
};

//validate item & calculate choicesets price for 'add to cart' button
const usePurchasedItemStatus = (
  purchased: NestedChoiceSelections,
  choiceSets: (ValidatedChoiceSet | ValidatedNestedChoiceSet)[],
  choiceSetDiscountConfig?: ChoiceSetQuantityDiscountConfig,
): [boolean, number] => {
  const choiceOrderingMethod = useAppSelector(state => state.ordering.config.choiceOrderingMethod);
  const [nestedItemInValid, setNestedItemInValid] = useState(false);
  const [nestedChoiceSetsTotal, setNestedChoiceSetsTotal] = useState(0);
  useEffect(() => {
    let choiceSelection: NestedChoiceSelections = {};
    if (Object.keys(purchased || {}).length > 0) {
      for (const p in purchased) {
        choiceSelection[p] = purchased[p];
      }
    }
    const cwq = nestedChoiceSetsWithQuantities(
      choiceSets,
      choiceSelection,
      choiceOrderingMethod || 'cheapest',
      choiceSetDiscountConfig,
    );
    const validatedSets = tallyAndValidateNestedChoicesets(cwq);

    let { moneyPrice: totalChoiceSetsPrice } = calculateChoiceSelectionsTotals(
      purchased,
      validatedSets,
      choiceOrderingMethod || 'cheapest',
      choiceSetDiscountConfig,
    );
    const miamSets: (ValidatedChoiceSet | ValidatedNestedChoiceSet)[] = [];
    getAllMiamChoiceSets(choiceSets, miamSets);
    miamSets.forEach(miamSet => {
      const miamQty = miamSet.quantity;
      const _miamTotalPrice = calculateMiamChoiceSetTotalPrice(
        miamSet,
        miamQty,
        choiceOrderingMethod || 'cheapest',
      );
      totalChoiceSetsPrice += _miamTotalPrice.moneyPrice;
    });

    setNestedChoiceSetsTotal(totalChoiceSetsPrice);

    const inValid = !!validatedSets.filter(set => !set.valid).length;
    setNestedItemInValid(inValid);
  }, [choiceSets, purchased, choiceOrderingMethod]);
  return [nestedItemInValid, nestedChoiceSetsTotal];
};

export const isChoiceInStagedPurchase = (
  choiceId: string,
  parentChoiceSetId: string,
  purchased: NestedChoiceSelections,
  nestedStaging: NestedIngredientsStages,
  nested: boolean,
): [boolean, number | undefined] => {
  let isChoiceInPurchase = false,
    selectedQty = undefined;
  if (!purchased || !nestedStaging) return [isChoiceInPurchase, selectedQty];
  const path = nested
    ? [nestedStaging[0] as string, nestedStaging[1] as string, parentChoiceSetId]
    : (nestedStaging[0] as string);
  if (!lodash.has(purchased, path)) return [isChoiceInPurchase, selectedQty];
  const choices = lodash.get(purchased, path);
  const arrLength = (choices as ChoiceWithQuantity[]).filter(c => c.plucode === choiceId).length;
  isChoiceInPurchase = Boolean(arrLength);
  selectedQty = arrLength ? arrLength : undefined;
  return [isChoiceInPurchase, selectedQty];
};

export const isPurchasedChoiceSet = (
  purchased: NestedChoiceSelections | undefined,
  id?: string,
  stages?: { [key: string]: string | { [key: string]: ChoiceWithQuantity[] } },
): boolean => {
  let result: boolean = false;

  if (!Object.keys(purchased || {}).length) return result;
  if (!stages) {
    for (const p in purchased) {
      if (Array.isArray(purchased[p]) && purchased[p].length === 0) continue;
      result = p === id;
      if (result) break;
    }
  } else {
    for (const item in purchased) {
      if (Array.isArray(purchased[item]) && purchased[item].length > 0) {
        result = item === stages[0];
        if (result) return result;
      }
      if (item === stages[0]) {
        for (const i in purchased[item]) {
          if (id) {
            result = i === id;
          } else {
            if (typeof stages[1] === 'string') result = i === stages[1];
          }

          if (result) {
            break;
          }
        }
      }
      if (result) break;
    }
  }

  return result;
};

// TODO: combine this into one function
export const calculateChoiceSetPrice = (
  selection: SDict<SDict<ChoiceWithQuantity[]>> | ChoiceWithQuantity[],
  choiceSet: ValidatedChoiceSet,
  nestedIngredients: boolean,
  choiceOrderingMethod: ChoiceOrderingMethod,
  choiceSetDiscountConfig?: ChoiceSetQuantityDiscountConfig,
): number => {
  let result = 0;
  if (nestedIngredients) {
    if (!selection) return result;
    // typescript throwing a few hissy fits with this function so did some dodgy casting
    // foreach nested item (choice) in choiceset

    Object.entries(selection).forEach(([choiceId, choiceVals]) => {
      // not sure what the correct type to use here is
      const choice = choiceSet.choices.find(c => c.id === choiceId) as unknown as NestedChoice;

      if (!choice) return;
      result += choice.baseMoneyPrice;
      // foreach nested choiceset in choice item
      Object.entries(choiceVals as SDict<ChoiceWithQuantity[]>).forEach(
        ([nestedChoiceSetId, nestedChoiceSetVals]) => {
          const nestedChoiceSet = choice.choiceSets.find(c => c.id === nestedChoiceSetId);

          result += sortChoices(nestedChoiceSetVals, choiceOrderingMethod).reduce(
            (sum, nestedChoice, i) =>
              sum + (i >= (nestedChoiceSet?.free ?? 0) ? nestedChoice.baseMoneyPrice : 0),
            0,
          );
        },
      );

      // Deducting the discount price of choice
      if (choiceSetDiscountConfig) {
        const { choiceSetKey, choiceSetDiscountMap } = choiceSetDiscountConfig;

        const targetChoice: any = choiceSet.choices.find(ch => ch.id === choiceId) || [];
        const discountChoiceSet = targetChoice?.choiceSets.find(
          (cs: any) => cs?.name?.toLowerCase() === choiceSetKey?.toLowerCase(),
        );

        const { plus } = choiceSetDiscountPlus(choiceSetDiscountMap || []);

        if (discountChoiceSet && plus.length) {
          const discountChoices =
            discountChoiceSet.choices.filter((c: any) => plus.includes(c.plucode)) || [];

          discountChoices.forEach((ch: any) => {
            result += ch.moneyPrice;
          });
        }
      }
    });
  } else {
    result += ((selection as ChoiceWithQuantity[]) || []).reduce(
      (sum: number, choice: ChoiceWithQuantity, i) =>
        sum + (i >= choiceSet.free ? choice.baseMoneyPrice : 0),
      0,
    );
  }

  return result;
};

export const createPurchaseFromStages = (
  stagedSet: NestedIngredientsStages,
): NestedChoiceSelections | SDict<ChoiceWithQuantity[]> => {
  return Object.keys(stagedSet || {}).length === 3
    ? {
        [stagedSet!['0'] as string]: {
          [stagedSet!['1'] as string]: stagedSet!['2'] as SDict<ChoiceWithQuantity[]>,
        },
      }
    : (stagedSet!['1'] as SDict<ChoiceWithQuantity[]>);
};

//calculate preselect choice quantity when populate preselection
const calculateChoiceQuantityInPreselect = (
  preSelect: NestedChoiceSelections,
  choiceSets: (ValidatedChoiceSet | ValidatedNestedChoiceSet)[] | undefined,
  stockBalanceDataMap: SDict<StockBalanceData> | undefined,
): SDict<number | undefined> | undefined => {
  if (!stockBalanceDataMap) return;
  if (!preSelect) return lodash.mapValues(stockBalanceDataMap, 'cartAdjustedBalance');

  const selectedChoices = extractChoiceWithQuantityFromNestedPurchased(
    preSelect,
    choiceSets as (ChoiceSet | NestedChoiceSet)[],
  );
  const preSelectChoices = (selectedChoices || []).reduce((acc, current) => {
    return {
      ...acc,
      [current.plucode]: lodash.has(acc, current.plucode) ? acc[current.plucode] + 1 : 1,
    };
  }, {} as SDict<number>);

  const cartAdjustedBalanceMap = lodash.mapValues(stockBalanceDataMap, 'cartAdjustedBalance');

  return lodash.mapValues(cartAdjustedBalanceMap, (value, key) => {
    if (lodash.has(preSelectChoices, key)) {
      return value ? value - preSelectChoices[key] : undefined;
    } else {
      return value;
    }
  });
};

//process pre selected item
const usePreSelections = (
  openPurchase: PurchaseWithTotals | undefined,
  choiceSets: (ValidatedChoiceSet | ValidatedNestedChoiceSet)[] | undefined,
  stockBalanceDataMap?: SDict<StockBalanceData>,
  choiceSetDiscountConfig?: ChoiceSetQuantityDiscountConfig,
) => {
  const [prePurchased, setPrePurchased] = useState<NestedChoiceSelections>();
  useEffect(() => {
    if (choiceSets) {
      let preSelect: NestedChoiceSelections;
      choiceSets.forEach(set => {
        const preSelectQuantityMap = calculateChoiceQuantityInPreselect(
          preSelect,
          choiceSets,
          stockBalanceDataMap,
        );
        const preSelectNestedChoices = set.choices.filter(
          c =>
            c.selected &&
            (!lodash.has(preSelectQuantityMap, c.plucode) ||
              (lodash.has(preSelectQuantityMap, c.plucode) &&
                (preSelectQuantityMap![c.plucode] || 0) > 0)),
        );
        if (preSelectNestedChoices.length) {
          if ('nestedIngredients' in set) {
            (preSelectNestedChoices as NestedChoiceWithQuantity[]).forEach(
              (nestedChoice: NestedChoiceWithQuantity) => {
                let nestedChoiceSelection: object | undefined = {};
                const nestedSets = nestedChoice.choiceSets ?? [];

                nestedSets.forEach(s => {
                  const { choices: nestedChoices } = s;
                  const selectedChoices = nestedChoices.filter(c => c.selected);
                  const preQty =
                    selectedChoices.length === 1 && s.min
                      ? Math.min(s.min, s.individualMax || s.min)
                      : 1;

                  if (preQty > 1) {
                    if (
                      lodash.has(preSelectQuantityMap, selectedChoices[0].plucode) &&
                      (preSelectQuantityMap as SDict<number>)[selectedChoices[0].plucode] !==
                        undefined &&
                      preQty < (preSelectQuantityMap![selectedChoices[0].plucode] as number)
                    ) {
                      nestedChoiceSelection = {
                        ...nestedChoiceSelection,
                        [s.id]: Array.from({ length: preQty }, () => selectedChoices[0]),
                      };
                    }
                  } else {
                    nestedChoiceSelection = {
                      ...nestedChoiceSelection,
                      [s.id]: selectedChoices.filter(
                        c =>
                          preSelectQuantityMap === undefined ||
                          preSelectQuantityMap[c.plucode] === undefined ||
                          !((preSelectQuantityMap[c.plucode] as number) < preQty),
                      ),
                    };
                  }
                });
                const selections = Object.entries(nestedChoiceSelection || {}).reduce(
                  (acc, [key, value]) => ({
                    ...acc,
                    [key]: (value as ChoiceWithQuantity[]).map(v => v.id),
                  }),
                  {},
                );
                const validatedSets = tallyAndValidateChoiceSets(
                  choiceSetsWithQuantities(nestedSets, selections),
                  choiceSetDiscountConfig,
                );
                const validated = validatedSets.reduce(
                  (acc, current) => acc && current.valid,
                  true,
                );
                if (validated)
                  preSelect = {
                    ...preSelect,
                    [set.key]: {
                      [nestedChoice.id]: nestedChoiceSelection as SDict<ChoiceWithQuantity[]>,
                    },
                  };
              },
            );
          } else {
            const preChoices = set.choices.filter(c => c.selected);
            const preQty =
              preChoices.length === 1 && set.min
                ? Math.min(set.min, set.individualMax || set.min)
                : 1;
            if (preChoices.length > 0) {
              const selection =
                preQty > 1 ? Array.from({ length: preQty }, () => preChoices[0]) : preChoices;
              const validatedSet = tallyAndValidateChoiceSets(
                choiceSetsWithQuantities([set], { [set.key]: selection.map(s => s.id) }),
                choiceSetDiscountConfig,
              )[0];

              if (validatedSet.valid)
                preSelect = {
                  ...preSelect,
                  [set.key]: selection,
                };
            }
          }
        }
      });
      setPrePurchased(preSelect);
    }
  }, [openPurchase]);

  return prePurchased;
};
